<template>
  <div class="admin-breakdow">
    <admin-view-header title="Kontakty awaryjne">
      <div class="buttons">
        <button class="button is-danger is-outlined" @click="resetForm">
          <icon name="fa-trash" />
          <span>Wyczyść</span>
        </button>
        <button class="button is-primary is-outlined" @click="showPreview">
          <icon name="fa-eye" />
          <span>Podgląd</span>
        </button>
        <protected :with="Permission.CREATE_TEMPLATE">
          <button
            class="button is-primary"
            @click="submit"
            :class="{ 'is-loading': submitting }"
            :disabled="submitting"
          >
            <icon name="fa-check" />
            <span>Zapisz</span>
          </button>
        </protected>
      </div>
    </admin-view-header>

    <tabs :tabs="tabs" v-model="tab">
      <template #general>
        <div class="field">
          <label class="label">{{ FIELDS[T.Contact.BREAKDOWN_HEADER] }}</label>
          <editor
            frame
            placeholder="Opcjonalna informacja dotycząca numerów awaryjnych..."
            v-model="fields[T.Contact.BREAKDOWN_HEADER]"
            :readonly="!hasPermission(Permission.CREATE_TEMPLATE)"
          />
        </div>
      </template>

      <template #breakdownTv>
        <loader :state="actions.templates">
          <div>
            <transition-group name="fade" class="breakdown-contacts" tag="div">
              <div
                class="breakdown-contact box"
                v-for="(contact, contactIndex) in fields[T.Contact.BREAKDOWN]
                  .tv"
                :key="contact.id"
              >
                <div class="breakdown-contact__item breakdown-contact__phone">
                  <icon name="fa-phone-alt" />
                  <span>{{ contact.phone }}</span>
                </div>
                <div
                  class="breakdown-contact__item breakdown-contact__availability"
                >
                  <div>
                    <p
                      v-for="(availability,
                      availabilityIndex) in contact.availability"
                      :key="availabilityIndex"
                    >
                      <dynamic-content :content="availability" />
                    </p>
                  </div>
                </div>
                <protected :with="Permission.CREATE_TEMPLATE">
                  <div
                    class="breakdown-contact__item breakdown-contact__controls"
                  >
                    <icon
                      class="breakdown-contact-control breakdown-contact-control--remove"
                      name="fa-trash"
                      data-tooltip="Usuń biuro"
                      @click="removeBreakdownContact('tv', contactIndex)"
                    />
                    <icon
                      class="breakdown-contact-control"
                      name="fa-arrow-left"
                      data-tooltip="Przesuń w lewo"
                      @click="moveBreakdownContactToLeft('tv', contactIndex)"
                      v-if="contactIndex > 0"
                    />
                    <icon
                      class="breakdown-contact-control"
                      name="fa-arrow-right"
                      data-tooltip="Przesuń w prawo"
                      @click="moveBreakdownContactToRight('tv', contactIndex)"
                      v-if="
                        contactIndex < fields[T.Contact.BREAKDOWN].tv.length - 1
                      "
                    />
                    <icon
                      class="breakdown-contact-control"
                      name="fa-pen"
                      data-tooltip="Edytuj biuro"
                      @click="editBreakdownContact('tv', contactIndex)"
                    />
                  </div>
                </protected>
              </div>
              <protected
                :with="Permission.CREATE_TEMPLATE"
                key="add-breakdown-contact"
              >
                <div class="add-breakdown-contact">
                  <icon
                    name="fa-plus-circle"
                    @click="addBreakdownContact('tv')"
                  />
                </div>
              </protected>
            </transition-group>
          </div>

          <template #loading>
            <div
              class="spinner is-3by4 is-grey-lighter is-thick is-large is-centered"
            ></div>
          </template>

          <template #error>
            <div class="grey-message">
              <icon name="fa-times-circle" />
              <p>Dane niedostępne z powodu braku połączenia z serwerem.</p>
            </div>
          </template>
        </loader>
      </template>

      <template #breakdownInternet>
        <loader :state="actions.templates">
          <div>
            <transition-group name="fade" class="breakdown-contacts" tag="div">
              <div
                class="breakdown-contact box"
                v-for="(contact, contactIndex) in fields[T.Contact.BREAKDOWN]
                  .internet"
                :key="contact.id"
              >
                <div class="breakdown-contact__item breakdown-contact__phone">
                  <icon name="fa-phone-alt" />
                  <span>{{ contact.phone }}</span>
                </div>
                <div
                  class="breakdown-contact__item breakdown-contact__availability"
                >
                  <div>
                    <p
                      v-for="(availability,
                      availabilityIndex) in contact.availability"
                      :key="availabilityIndex"
                    >
                      <dynamic-content :content="availability" />
                    </p>
                  </div>
                </div>
                <protected :with="Permission.CREATE_TEMPLATE">
                  <div
                    class="breakdown-contact__item breakdown-contact__controls"
                  >
                    <icon
                      class="breakdown-contact-control breakdown-contact-control--remove"
                      name="fa-trash"
                      data-tooltip="Usuń biuro"
                      @click="removeBreakdownContact('internet', contactIndex)"
                    />
                    <icon
                      class="breakdown-contact-control"
                      name="fa-arrow-left"
                      data-tooltip="Przesuń w lewo"
                      @click="
                        moveBreakdownContactToLeft('internet', contactIndex)
                      "
                      v-if="contactIndex > 0"
                    />
                    <icon
                      class="breakdown-contact-control"
                      name="fa-arrow-right"
                      data-tooltip="Przesuń w prawo"
                      @click="
                        moveBreakdownContactToRight('internet', contactIndex)
                      "
                      v-if="
                        contactIndex <
                          fields[T.Contact.BREAKDOWN].internet.length - 1
                      "
                    />
                    <icon
                      class="breakdown-contact-control"
                      name="fa-pen"
                      data-tooltip="Edytuj biuro"
                      @click="editBreakdownContact('internet', contactIndex)"
                    />
                  </div>
                </protected>
              </div>
              <protected
                :with="Permission.CREATE_TEMPLATE"
                key="add-breakdown-contact"
              >
                <div class="add-breakdown-contact">
                  <icon
                    name="fa-plus-circle"
                    @click="addBreakdownContact('internet')"
                  />
                </div>
              </protected>
            </transition-group>
          </div>

          <template #loading>
            <div
              class="spinner is-3by4 is-grey-lighter is-thick is-large is-centered"
            ></div>
          </template>

          <template #error>
            <div class="grey-message">
              <icon name="fa-times-circle" />
              <p>Dane niedostępne z powodu braku połączenia z serwerem.</p>
            </div>
          </template>
        </loader>
      </template>
    </tabs>
  </div>
</template>

<script>
import {
  Protected,
  WithTemplates,
  WithUnsavedChangesConfirmation,
  WithPreview,
  WithReset
} from "%mixins";

import Editor from "%admin/components/global/Editor";

import { Icon, Tabs, DynamicContent } from "%helpers";
import AdminViewHeader from "%admin/components/global/AdminViewHeader";

import BreakdownModal from "%modals/BreakdownModal";

import T from "%const/templates";

const FIELDS = {
  [T.Contact.BREAKDOWN_HEADER]: "Informacja o kontaktach awaryjnych",
  [T.Contact.BREAKDOWN]: "Telefony awaryjne"
};

export default {
  name: "admin-breakdown",

  components: {
    Icon,
    AdminViewHeader,
    Tabs,
    Editor,
    DynamicContent
  },

  mixins: [
    Protected,

    WithTemplates({
      templates: [T.Contact.BREAKDOWN_HEADER, T.Contact.BREAKDOWN],
      json: [T.Contact.BREAKDOWN]
    }),

    WithUnsavedChangesConfirmation({
      checkChanges() {
        return (
          this.templatesLoaded() && this.areAnyTemplateChanges(this.fields)
        );
      },

      async onSave() {
        return await this.submit();
      }
    }),

    WithPreview({
      path: "/home",
      query: {
        step: "_breakdown-contact"
      }
    }),

    WithReset(function() {
      this.initData();
    })
  ],

  data() {
    return {
      T,
      FIELDS,

      tabs: {
        general: {
          icon: "fa-info-circle",
          text: "Ogólne"
        },

        breakdownTv: {
          icon: "fa-tv",
          text: "Kontakty awaryjne dla TV"
        },

        breakdownInternet: {
          icon: "fa-globe-europe",
          text: "Kontakty awaryjne dla Internetu"
        }
      },

      tab: "general",

      fields: {
        [T.Contact.BREAKDOWN_HEADER]: "",
        [T.Contact.BREAKDOWN]: { desc: "", tv: [], internet: [] }
      },

      submitting: false
    };
  },

  async mounted() {
    await this.requestTemplates();
    this.initData();
  },

  methods: {
    async submit() {
      return await this.updateChangedTemplates(
        this.fields,
        FIELDS,
        () => (this.submitting = true),
        () => (this.submitting = false)
      );
    },

    initData() {
      this.applyTemplates(this.fields, {
        [T.Contact.BREAKDOWN]: breakdown => ({
          desc: breakdown.desc,
          tv: breakdown.tv.map(e => ({ ...e, id: Symbol() })),
          internet: breakdown.internet.map(e => ({ ...e, id: Symbol() }))
        })
      });
    },

    addBreakdownContact(type) {
      this.$emit("show-modal", {
        component: BreakdownModal,
        props: {
          onSave: contact =>
            this.fields[T.Contact.BREAKDOWN][type].push({
              id: Symbol(),
              ...contact
            })
        }
      });
    },

    editBreakdownContact(type, index) {
      this.$emit("show-modal", {
        component: BreakdownModal,
        props: {
          value: this.fields[T.Contact.BREAKDOWN][type][index],
          onSave: contact =>
            this.fields[T.Contact.BREAKDOWN][type].splice(index, 1, contact),
          edit: true
        }
      });
    },

    moveBreakdownContactToLeft(type, index) {
      if (index > 0) {
        this.swapBreakdownContacts(type, index, index - 1);
      }
    },

    moveBreakdownContactToRight(type, index) {
      if (index < this.fields[T.Contact.BREAKDOWN][type].length - 1) {
        this.swapBreakdownContacts(type, index, index + 1);
      }
    },

    swapBreakdownContacts(type, a, b) {
      const tmp = this.fields[T.Contact.BREAKDOWN][type][a];
      this.fields[T.Contact.BREAKDOWN][type].splice(
        a,
        1,
        this.fields[T.Contact.BREAKDOWN][type][b]
      );
      this.fields[T.Contact.BREAKDOWN][type].splice(b, 1, tmp);
    },

    removeBreakdownContact(type, index) {
      this.fields[T.Contact.BREAKDOWN][type].splice(index, 1);
    }
  }
};
</script>

<style lang="scss" scoped>
.breakdown-contacts {
  display: flex;
  flex-flow: row wrap;
  width: 100%;

  .breakdown-contact {
    margin: 0.3rem;
    min-width: 20rem;
    max-width: 25rem;
    display: flex;
    flex-flow: column nowrap;
    justify-content: space-between;
    transition: 0.1s;

    &__item {
      margin: 0.3rem;
      display: flex;

      .icon {
        margin-right: 0.5rem;
      }
    }

    &__name {
      font-weight: 700;
    }

    &__map {
      .map {
        height: 10rem;
        z-index: 0;
      }
    }

    &__controls {
      margin-top: 1rem;
      display: flex;
      justify-content: space-between;

      .breakdown-contact-control {
        color: $grey-lighter;
        cursor: pointer;

        &:not(:last-child) {
          margin-right: 1rem;
        }

        &:hover {
          color: $grey-light;
        }

        &--remove:hover {
          color: $danger;
        }
      }
    }
  }

  .add-breakdown-contact {
    margin: 0.3rem;
    width: 10rem;
    color: $grey-lighter;
    font-size: 5rem;
    display: flex;
    justify-content: center;
    align-items: center;

    .icon {
      cursor: pointer;
      &:hover {
        color: $grey;
      }
    }
  }
}
</style>
